import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Pencil,
  Phone,
  Plus,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { useState } from 'react';
import {
  type EmergencyContactDto,
  useCreateContact,
  useDeleteContact,
  useUpdateContact,
  useUserEmergencyContacts,
} from '../api/useEmergencyContacts.js';
import { cn } from '../lib/cn.js';
import { formatRelative } from '../lib/format.js';

// Manage a worker's emergency contacts. Admin only; shown as a modal over the Roster.
// Each row is inline-editable (edit / delete / toggle notification flags / set primary).
//
// Primary flag is mutually-exclusive per worker — the service demotes any previous
// primary on promotion, so the UI just flips the chosen row.

export function EmergencyContactsModal({
  userId,
  userName,
  onClose,
}: {
  userId: string;
  userName: string;
  onClose: () => void;
}) {
  const query = useUserEmergencyContacts(userId);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<EmergencyContactDto | null>(null);

  const contacts = query.data?.contacts ?? [];
  const primary = contacts.find((c) => c.is_primary);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="contacts-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg bg-white p-5 shadow-2xl"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="contacts-title" className="text-base font-semibold text-ink">
              Emergency contacts · {userName}
            </h2>
            <p className="mt-0.5 text-xs text-ink-faint">
              These numbers receive duress + overdue SMS. The primary is called first by the
              monitoring team.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-ink-mute hover:bg-surface-3 hover:text-ink-dim"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {query.isLoading ? (
            <div className="py-6 text-center text-xs text-ink-faint">Loading…</div>
          ) : contacts.length === 0 && !addOpen ? (
            <div className="rounded-md border border-dashed border-line-strong bg-surface-3 px-4 py-6 text-center">
              <Phone className="mx-auto mb-2 h-5 w-5 text-ink-mute" />
              <div className="text-sm font-medium text-ink">No contacts yet</div>
              <div className="mb-3 text-xs text-ink-faint">
                Add at least one so duress alerts have someone to reach.
              </div>
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple"
              >
                <Plus className="h-3.5 w-3.5" />
                Add contact
              </button>
            </div>
          ) : (
            <ul className="flex flex-col divide-y divide-line rounded-md border border-line bg-white">
              {contacts.map((c) => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  isEditing={editing?.id === c.id}
                  onEdit={() => setEditing(c)}
                  onCancelEdit={() => setEditing(null)}
                  onSaved={() => setEditing(null)}
                />
              ))}
            </ul>
          )}

          {addOpen && (
            <div className="mt-3 rounded-md border border-brand-purple/30 bg-brand-purple/5 p-3">
              <ContactForm
                mode="create"
                userId={userId}
                hasExistingPrimary={Boolean(primary)}
                onCancel={() => setAddOpen(false)}
                onSaved={() => setAddOpen(false)}
              />
            </div>
          )}
        </div>

        {contacts.length > 0 && !addOpen && (
          <div className="mt-3 flex justify-end border-t border-line pt-3">
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-white px-3 py-1.5 text-xs font-medium text-ink-dim hover:border-brand-purple hover:text-brand-purple"
            >
              <Plus className="h-3.5 w-3.5" />
              Add another
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ContactRow({
  contact,
  isEditing,
  onEdit,
  onCancelEdit,
  onSaved,
}: {
  contact: EmergencyContactDto;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaved: () => void;
}) {
  const del = useDeleteContact();
  const update = useUpdateContact();

  if (isEditing) {
    return (
      <li className="bg-brand-purple/5 p-3">
        <ContactForm
          mode="edit"
          userId={contact.user_id ?? ''}
          existing={contact}
          hasExistingPrimary={false /* editing an existing row — promotion is just a flag flip */}
          onCancel={onCancelEdit}
          onSaved={onSaved}
        />
      </li>
    );
  }

  async function setAsPrimary() {
    if (contact.is_primary) return;
    await update.mutateAsync({ id: contact.id, is_primary: true });
  }

  async function toggleDuress() {
    await update.mutateAsync({ id: contact.id, notify_on_duress: !contact.notify_on_duress });
  }

  async function toggleOverdue() {
    await update.mutateAsync({ id: contact.id, notify_on_overdue: !contact.notify_on_overdue });
  }

  return (
    <li className="flex items-center gap-3 p-3 hover:bg-surface-3">
      <button
        type="button"
        onClick={setAsPrimary}
        disabled={contact.is_primary || update.isPending}
        title={contact.is_primary ? 'Primary contact' : 'Set as primary'}
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-full transition',
          contact.is_primary
            ? 'bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-200'
            : 'text-ink-mute hover:bg-amber-50 hover:text-amber-600',
        )}
      >
        <Star className={cn('h-3.5 w-3.5', contact.is_primary && 'fill-current')} />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink">{contact.name}</span>
          {contact.role && <span className="text-xs text-ink-faint">· {contact.role}</span>}
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-faint">
          {contact.phone_e164 && <span className="font-mono">{contact.phone_e164}</span>}
          {contact.email && <span className="font-mono">{contact.email}</span>}
          <span>·</span>
          <span>added {formatRelative(contact.created_at)}</span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={toggleDuress}
          title={`Notify on duress: ${contact.notify_on_duress ? 'on' : 'off'}`}
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset transition',
            contact.notify_on_duress
              ? 'bg-red-50 text-red-700 ring-red-200'
              : 'bg-surface-3 text-ink-mute ring-gray-200',
          )}
        >
          duress
        </button>
        <button
          type="button"
          onClick={toggleOverdue}
          title={`Notify on overdue: ${contact.notify_on_overdue ? 'on' : 'off'}`}
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset transition',
            contact.notify_on_overdue
              ? 'bg-amber-50 text-amber-700 ring-amber-200'
              : 'bg-surface-3 text-ink-mute ring-gray-200',
          )}
        >
          overdue
        </button>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="rounded p-1.5 text-ink-faint hover:bg-surface-3 hover:text-ink"
          title="Edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          disabled={del.isPending}
          onClick={() => {
            if (window.confirm(`Remove ${contact.name}?`)) del.mutate(contact.id);
          }}
          className="rounded p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-40"
          title="Remove"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
}

function ContactForm({
  mode,
  userId,
  existing,
  hasExistingPrimary,
  onCancel,
  onSaved,
}: {
  mode: 'create' | 'edit';
  userId: string;
  existing?: EmergencyContactDto;
  hasExistingPrimary: boolean;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(existing?.name ?? '');
  const [role, setRole] = useState(existing?.role ?? '');
  const [phone, setPhone] = useState(existing?.phone_e164 ?? '');
  const [isPrimary, setIsPrimary] = useState(existing?.is_primary ?? false);
  const [notifyDuress, setNotifyDuress] = useState(existing?.notify_on_duress ?? true);
  const [notifyOverdue, setNotifyOverdue] = useState(existing?.notify_on_overdue ?? true);
  const create = useCreateContact();
  const update = useUpdateContact();
  const mut = mode === 'create' ? create : update;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (mode === 'create') {
        await create.mutateAsync({
          user_id: userId,
          name,
          role: role.trim() || null,
          phone_e164: phone,
          is_primary: isPrimary,
          notify_on_duress: notifyDuress,
          notify_on_overdue: notifyOverdue,
        });
      } else if (existing) {
        await update.mutateAsync({
          id: existing.id,
          name: name !== existing.name ? name : undefined,
          role: (role.trim() || null) !== existing.role ? role.trim() || null : undefined,
          phone_e164: phone !== existing.phone_e164 ? phone : undefined,
          is_primary: isPrimary !== existing.is_primary ? isPrimary : undefined,
          notify_on_duress: notifyDuress !== existing.notify_on_duress ? notifyDuress : undefined,
          notify_on_overdue:
            notifyOverdue !== existing.notify_on_overdue ? notifyOverdue : undefined,
        });
      }
      onSaved();
    } catch {
      /* error surfaced below */
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium text-ink-dim">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={120}
            className="rounded-md border border-line-strong px-2.5 py-1 text-sm focus:border-brand-purple focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium text-ink-dim">Role (optional)</span>
          <input
            type="text"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Partner, Manager, GP…"
            maxLength={120}
            className="rounded-md border border-line-strong px-2.5 py-1 text-sm focus:border-brand-purple focus:outline-none"
          />
        </label>
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-medium text-ink-dim">Mobile number</span>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
          placeholder="+61 412 345 678"
          maxLength={24}
          className="rounded-md border border-line-strong px-2.5 py-1 font-mono text-sm focus:border-brand-purple focus:outline-none"
        />
      </label>

      <div className="flex flex-wrap items-center gap-3 text-xs">
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-line-strong text-brand-purple"
          />
          Primary
          {isPrimary && hasExistingPrimary && (
            <span className="text-[10px] text-amber-700">(will demote current primary)</span>
          )}
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={notifyDuress}
            onChange={(e) => setNotifyDuress(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-line-strong text-brand-purple"
          />
          Notify on duress
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={notifyOverdue}
            onChange={(e) => setNotifyOverdue(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-line-strong text-brand-purple"
          />
          Notify on overdue
        </label>
      </div>

      {mut.isError && (
        <div className="inline-flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-[11px] text-red-800">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          {(mut.error as Error).message}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-line bg-white px-2.5 py-1 text-xs font-medium text-ink-dim hover:bg-surface-3"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!name.trim() || !phone.trim() || mut.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-brand-purple px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
        >
          {mut.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <CheckCircle2 className="h-3 w-3" />
          )}
          {mode === 'create' ? 'Add contact' : 'Save'}
        </button>
      </div>
    </form>
  );
}
